import { Bone, Box3, Object3D, Mesh, SkinnedMesh, Vector3 } from 'three';

const aliases: Record<string, string[]> = {
  // Order matters: the first alias present on a rig wins. Tripo rigs have both
  // Hip (moves the whole body) and Pelvis (just the legs' parent), so hip must
  // come before pelvis; its spine chain is Waist → Spine01 → Spine02.
  hips: ['hips', 'hip', 'pelvis'], spine: ['spine', 'waist'], chest: ['spine1', 'spine01', 'chest'],
  upperChest: ['spine2', 'spine02', 'upperchest'], neck: ['neck', 'necktwist01'], head: ['head'],
  leftEye: ['lefteye', 'eyel'], rightEye: ['righteye', 'eyer'], jaw: ['jaw'],
};
for (const side of ['left', 'right']) {
  for (const [key, names] of Object.entries({ Shoulder: ['shoulder', 'clavicle'], UpperArm: ['arm', 'upperarm'], LowerArm: ['forearm', 'lowerarm'], Hand: ['hand'], UpperLeg: ['upleg', 'upperleg', 'thigh'], LowerLeg: ['leg', 'lowerleg', 'calf'], Foot: ['foot'], Toes: ['toebase', 'toes'] })) {
    // "LeftForeArm" (Meshy/Mixamo) and "L_Forearm" (Tripo) both normalise here.
    aliases[side + key] = [...names.map(n => side + n), ...names.map(n => side[0] + n)];
  }
  for (const finger of ['Thumb', 'Index', 'Middle', 'Ring', 'Pinky']) {
    for (let joint = 1; joint <= 3; joint++) {
      aliases[`${side}${finger}${joint}`] = [`${side}hand${finger}${joint}`, `${side}${finger}${joint}`].map(n => n.toLowerCase());
    }
  }
}
const normalized = (name: string) => name.toLowerCase().replace(/^.*:/, '').replace(/^mixamorig/, '').replace(/[^a-z0-9]/g, '');
export const HUMANOID_BONES = Object.keys(aliases);
const keyByName = new Map<string, string>();
for (const [key, names] of Object.entries(aliases)) for (const n of names) if (!keyByName.has(n)) keyByName.set(n, key);
/** Canonical humanoid key for a bone name on any supported rig (Meshy, Mixamo, Tripo), or null. */
export function humanoidKey(name: string): string | null { return keyByName.get(normalized(name)) ?? null; }
/** Tripo's own skeleton (spec "tripo"): L_/R_ limbs, Hip/Waist spine, twist bones. */
export function isTripoRig(root: Object3D): boolean {
  let tripo = false;
  root.traverse(node => { if (!tripo && (node as Bone).isBone && /^L_Upperarm$/.test(node.name)) tripo = true; });
  return tripo;
}
const faceAliases: Record<string, string[]> = {
  blinkLeft: ['eyeblinkleft', 'blinkleft', 'blinkl'], blinkRight: ['eyeblinkright', 'blinkright', 'blinkr'],
  jawOpen: ['jawopen', 'mouthopen'], smileLeft: ['mouthsmileleft', 'smileleft'], smileRight: ['mouthsmileright', 'smileright'],
  smile: ['smile'], browUp: ['browinnerup', 'browup'],
};
for (const name of ['sil', 'PP', 'FF', 'TH', 'DD', 'kk', 'CH', 'SS', 'nn', 'RR', 'aa', 'E', 'ih', 'oh', 'ou']) faceAliases[`viseme_${name}`] = [normalized(`viseme_${name}`)];
export interface BoneBinding {
  path: number[]; name: string; parentPath: number[] | null;
  position: number[]; quaternion: number[]; scale: number[]; modelPosition: number[];
}
export interface AvatarManifest {
  version: 1; rig: 'gathr_humanoid_v1'; model: string;
  pose: 'loaded-pose-unverified'; height: number | null; units: 'gltf-meters';
  bones: Record<string, BoneBinding>;
  face: Record<string, { path: number[]; mesh: string; index: number; name: string }[]>;
  measurements: Record<string, number>;
  capabilities: { bodyMapped: boolean; armIKCandidate: boolean; footIKCandidate: boolean; eyeBones: boolean; blink: boolean; jaw: boolean; visemes: boolean; fingerBones: boolean; secondaryMotion: false };
  warnings: string[]; boneCount: number; skinnedMeshCount: number;
}
export interface AvatarRig { manifest: AvatarManifest; bones: ReadonlyMap<string, Bone>; }
export function resolveRigNode(root: Object3D, path: number[]): Object3D | undefined {
  let node: Object3D | undefined = root;
  for (const index of path) node = node?.children[index];
  return node;
}

/** Observe the loaded rig. Never rename bones, rebind skinning, or change its pose. */
export function inspectAvatarRig(root: Object3D, model = ''): AvatarRig {
  root.updateWorldMatrix(true, true);
  const paths = new Map<Object3D, number[]>();
  const visit = (node: Object3D, path: number[]) => { paths.set(node, path); node.children.forEach((child, i) => visit(child, [...path, i])); };
  visit(root, []);
  const candidates = new Map<string, Bone[]>(), skinBones = new Set<Bone>();
  let skinnedMeshCount = 0;
  root.traverse(node => {
    const mesh = node as SkinnedMesh;
    if (mesh.isSkinnedMesh) { skinnedMeshCount++; mesh.skeleton.bones.forEach(b => skinBones.add(b)); }
  });
  // A free-standing bone named Head must not be confused with the deforming head.
  root.traverse(node => {
    if (!(node as Bone).isBone || (skinBones.size > 0 && !skinBones.has(node as Bone))) return;
    const name = normalized(node.name);
    candidates.set(name, [...(candidates.get(name) || []), node as Bone]);
  });
  const bones = new Map<string, Bone>(), warnings: string[] = [];
  for (const [key, names] of Object.entries(aliases)) {
    const first = names.find(name => candidates.has(name));
    const matches = first ? candidates.get(first)! : [];
    if (matches.length === 1) bones.set(key, matches[0]);
    else if (matches.length > 1) warnings.push(`Ambiguous ${key}: ${matches.map(b => b.name).join(', ')}. Manual mapping required.`);
  }
  const inverse = root.matrixWorld.clone().invert();
  const modelPosition = (node: Object3D) => node.getWorldPosition(new Vector3()).applyMatrix4(inverse);
  const bindings: AvatarManifest['bones'] = {};
  for (const [key, bone] of bones) bindings[key] = { path: paths.get(bone)!, name: bone.name, parentPath: bone.parent ? paths.get(bone.parent) ?? null : null, position: bone.position.toArray(), quaternion: bone.quaternion.toArray(), scale: bone.scale.toArray(), modelPosition: modelPosition(bone).toArray() };
  const measurements: Record<string, number> = {};
  const chain = (names: string[]) => {
    if (!names.every(n => bones.has(n))) return false;
    for (let i = 1; i < names.length; i++) {
      const a = bones.get(names[i - 1])!, b = bones.get(names[i])!;
      let parent = b.parent;
      while (parent && parent !== a) parent = parent.parent;
      const length = modelPosition(a).distanceTo(modelPosition(b));
      if (!parent || !Number.isFinite(length) || length < .00001) { warnings.push(`Invalid chain ${names[i - 1]} → ${names[i]}. Check hierarchy and joint spacing.`); return false; }
      measurements[`${names[i - 1]}To${names[i]}`] = length;
    }
    return skinnedMeshCount > 0;
  };
  const bodyMapped = chain(['hips', 'spine', 'neck', 'head']);
  const leftArm = chain(['spine', 'leftUpperArm', 'leftLowerArm', 'leftHand']);
  const rightArm = chain(['spine', 'rightUpperArm', 'rightLowerArm', 'rightHand']);
  const leftLeg = chain(['hips', 'leftUpperLeg', 'leftLowerLeg', 'leftFoot']);
  const rightLeg = chain(['hips', 'rightUpperLeg', 'rightLowerLeg', 'rightFoot']);
  const face: AvatarManifest['face'] = {};
  root.traverse(node => {
    const mesh = node as SkinnedMesh;
    for (const [name, index] of Object.entries(mesh.morphTargetDictionary || {})) {
      if (!mesh.morphTargetInfluences || index < 0 || index >= mesh.morphTargetInfluences.length) continue;
      for (const [key, names] of Object.entries(faceAliases)) if (names.includes(normalized(name))) {
        (face[key] ||= []).push({ path: paths.get(node)!, mesh: node.name, index, name });
      }
    }
  });
  const localBox = new Box3();
  root.traverse(node => {
    const mesh = node as Mesh;
    if (!mesh.isMesh) return;
    let bounds: Box3 | null;
    if ((mesh as SkinnedMesh).isSkinnedMesh) {
      (mesh as SkinnedMesh).computeBoundingBox();
      bounds = (mesh as SkinnedMesh).boundingBox;
    } else {
      mesh.geometry.computeBoundingBox();
      bounds = mesh.geometry.boundingBox;
    }
    if (bounds) localBox.union(bounds.clone().applyMatrix4(inverse.clone().multiply(mesh.matrixWorld)));
  });
  const rawHeight = localBox.isEmpty() ? NaN : localBox.max.y - localBox.min.y;
  const height = Number.isFinite(rawHeight) && rawHeight > 0 ? rawHeight : null;
  warnings.push('Loaded pose, facing direction and joint limits require review before IK. Measurements are not a bind-pose certification.');
  if (!skinnedMeshCount) warnings.push('No skinned mesh found; this asset is not ready for skeletal motion.');
  if (!bodyMapped) warnings.push('Core humanoid chain is incomplete or invalid.');
  if (!face.blinkLeft || !face.blinkRight) warnings.push('Paired blink controls not detected.');
  if (!face.jawOpen && !bones.has('jaw')) warnings.push('No jaw control detected; voice-driven mouth motion needs rig authoring.');
  const fingerBones = ['left', 'right'].every(side => ['Thumb', 'Index', 'Middle', 'Ring', 'Pinky'].every(finger => [1, 2, 3].every(j => bones.has(`${side}${finger}${j}`))));
  return { bones, manifest: { version: 1, rig: 'gathr_humanoid_v1', model, pose: 'loaded-pose-unverified', height, units: 'gltf-meters', bones: bindings, face, measurements,
    capabilities: { bodyMapped, armIKCandidate: bodyMapped && leftArm && rightArm, footIKCandidate: bodyMapped && leftLeg && rightLeg, eyeBones: bones.has('leftEye') && bones.has('rightEye'), blink: !!face.blinkLeft && !!face.blinkRight, jaw: !!face.jawOpen || bones.has('jaw'), visemes: ['aa', 'E', 'ih', 'oh', 'ou'].every(n => !!face[`viseme_${n}`]), fingerBones, secondaryMotion: false }, warnings, boneCount: [...candidates.values()].reduce((sum, list) => sum + list.length, 0), skinnedMeshCount } };
}

/** Bind an exported manifest to a structurally identical clone, refusing stale mappings. */
export function bindAvatarRig(root: Object3D, manifest: AvatarManifest): ReadonlyMap<string, Bone> {
  const bones = new Map<string, Bone>();
  if (manifest.version !== 1 || manifest.rig !== 'gathr_humanoid_v1') throw new Error('Unsupported avatar manifest');
  for (const [key, binding] of Object.entries(manifest.bones)) {
    const node = resolveRigNode(root, binding.path);
    if (!node || !(node as Bone).isBone || node.name !== binding.name) throw new Error(`Avatar rig mismatch: ${key}`);
    bones.set(key, node as Bone);
  }
  return bones;
}
